import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Truck,
  LogOut,
  MapPin,
  Phone,
  Package,
  CheckCircle2,
  Navigation,
  XCircle,
  RefreshCw,
  Loader2,
  MessageCircle,
  Map as MapIcon,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { useDriverAuth, API } from '../context/DriverAuthContext';
import { useToast } from '../hooks/use-toast';
import PushBell from '../components/PushBell';
import PushEnableBanner from '../components/PushEnableBanner';
import NotificationsBell from '../components/NotificationsBell';
import { orderPartyPrimary, orderPartyContact } from '../lib/orderParty';
import OrderChat from '../components/OrderChat';
import PWAInstallPrompt from '../components/PWAInstallPrompt';
import PasskeyEnrollCard from '../components/PasskeyEnrollCard';
import RoutePreview from '../components/RoutePreview';
import MultiStopRoute from '../components/MultiStopRoute';
import DriverPickupScanner from '../components/DriverPickupScanner';
import { openGoogleMapsNav } from '../lib/navigateTo';
import { useSettings } from '../context/SettingsContext';
import Rial from '../components/Rial';

const STATUS_LABELS = {
  assigned: 'Assigned',
  picked_up: 'Picked up',
  out_for_delivery: 'Out for delivery',
  delivered: 'Delivered',
  failed: 'Failed',
};

const STATUS_PILL = {
  assigned: 'bg-blue-500/15 text-blue-300 ring-1 ring-blue-400/30',
  picked_up: 'bg-amber-500/15 text-amber-300 ring-1 ring-amber-400/30',
  out_for_delivery: 'bg-purple-500/15 text-purple-300 ring-1 ring-purple-400/30',
  delivered: 'bg-emerald-500/15 text-emerald-300 ring-1 ring-emerald-400/30',
  failed: 'bg-red-500/15 text-red-300 ring-1 ring-red-400/30',
};

const NEXT_ACTIONS = {
  assigned: { next: 'picked_up', label: 'Mark picked up', icon: Package, tone: 'bg-amber-500 hover:bg-amber-600' },
  picked_up: { next: 'out_for_delivery', label: 'Start delivery', icon: Navigation, tone: 'bg-purple-500 hover:bg-purple-600' },
  out_for_delivery: { next: 'delivered', label: 'Mark delivered', icon: CheckCircle2, tone: 'bg-emerald-500 hover:bg-emerald-600' },
};

const DriverDashboard = () => {
  const { token, driver, logout, authHeaders } = useDriverAuth();
  const { settings } = useSettings();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [gpsOk, setGpsOk] = useState(null);
  const [chatOpen, setChatOpen] = useState(null); // order id with open chat
  // Order id whose in-app route preview is open. Only one at a time —
  // Leaflet maps are heavy, no point mounting more than one.
  const [routeOpen, setRouteOpen] = useState(null);
  // Briefly green-highlight the row of an order successfully verified
  // by the pickup scanner. Cleared by a timer.
  const [highlightId, setHighlightId] = useState(null);
  const rowRefs = useRef({});

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/driver/orders`, { headers: authHeaders });
      setOrders(data);
    } catch (err) {
      // ignore
    } finally {
      setLoading(false);
    }
  }, [authHeaders]);

  useEffect(() => {
    if (!token) return;
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [token, load]);

  // Live GPS streaming.
  //
  // Old approach: setInterval(ping, 20s) + maximumAge:15s → worst-case
  // 35s lag before the admin sees the new dot, often more.
  //
  // New approach: navigator.geolocation.watchPosition() — the browser
  // fires the callback as soon as a fresh fix is available (typically
  // 1-3s when moving). We throttle network writes to once every 5s so
  // we don't flood the backend, but the freshest available position is
  // always queued for the next slot. Combined with the admin's 5s poll,
  // total update lag drops from ~35s to ~10s.
  useEffect(() => {
    if (!token || !navigator.geolocation) return;
    let alive = true;
    let lastSent = 0;
    let pending = null; // most recent unsent position

    const flush = async () => {
      if (!alive || !pending) return;
      const { latitude: lat, longitude: lng } = pending;
      pending = null;
      lastSent = Date.now();
      try {
        await axios.post(
          `${API}/driver/location`,
          { lat, lng },
          { headers: authHeaders },
        );
        setGpsOk(true);
      } catch {
        setGpsOk(false);
      }
    };

    const handle = (pos) => {
      if (!alive) return;
      pending = pos.coords;
      // Send immediately if we haven't sent in the last 5s, else queue.
      if (Date.now() - lastSent >= 5000) flush();
    };

    const onError = () => {
      if (alive) setGpsOk(false);
    };

    const watchId = navigator.geolocation.watchPosition(handle, onError, {
      enableHighAccuracy: true,
      timeout: 15000,
      maximumAge: 0, // always demand a fresh fix — no stale cache
    });

    // Backup timer: even if watchPosition stops firing (browser background
    // throttling), flush any queued position every 5s and re-trigger
    // getCurrentPosition so the admin map keeps updating.
    const tick = setInterval(() => {
      if (pending) flush();
      else {
        navigator.geolocation.getCurrentPosition(handle, onError, {
          enableHighAccuracy: true,
          timeout: 8000,
          maximumAge: 5000,
        });
      }
    }, 5000);

    return () => {
      alive = false;
      navigator.geolocation.clearWatch(watchId);
      clearInterval(tick);
    };
  }, [token, authHeaders]);

  // Lightweight presence heartbeat — keeps the driver marked "online" even
  // when GPS is blocked, the driver isn't moving, or has no active assignment.
  useEffect(() => {
    if (!token) return undefined;
    let alive = true;
    const beat = async () => {
      try {
        await axios.post(`${API}/driver/heartbeat`, {}, { headers: authHeaders });
      } catch {
        // ignore — best-effort
      }
    };
    beat();
    const t = setInterval(() => { if (alive) beat(); }, 30000);
    const onVis = () => { if (alive && document.visibilityState === 'visible') beat(); };
    document.addEventListener('visibilitychange', onVis);
    return () => {
      alive = false;
      clearInterval(t);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [token, authHeaders]);

  if (!token) return <Navigate to="/driver/login" replace />;

  const updateStatus = async (o, next, failureReason = '') => {
    setBusyId(o.id);
    try {
      await axios.put(
        `${API}/driver/orders/${o.id}/status`,
        { status: next, failure_reason: failureReason },
        { headers: authHeaders },
      );
      toast({ title: 'Status updated', description: `${o.order_number || o.id.slice(0, 8)} → ${STATUS_LABELS[next]}` });
      await load();
    } catch (err) {
      toast({ title: 'Failed', description: err?.response?.data?.detail || 'Try again' });
    } finally {
      setBusyId(null);
    }
  };

  const markFailed = (o) => {
    const reason = window.prompt('Reason for failure?', 'Customer not reachable');
    if (!reason) return;
    updateStatus(o, 'failed', reason);
  };

  const active = orders.filter((o) => ['assigned', 'picked_up', 'out_for_delivery'].includes(o.status));
  const done = orders.filter((o) => ['delivered', 'failed'].includes(o.status));

  /**
   * Pickup scanner match handler — scroll the matched active-order row
   * into view, expand its details (auto-open the route preview so the
   * driver can immediately tap Navigate), and apply a 2.5s emerald
   * highlight ring so the driver visually confirms the right row was
   * scanned.
   *
   * Plain function (not useCallback) because this component has an
   * early-return guard above, and hooks must run unconditionally.
   * `DriverPickupScanner` doesn't memoize on prop identity, so a fresh
   * reference per render is fine.
   */
  const onScanMatch = (order) => {
    if (!order?.id) return;
    setRouteOpen(order.id);
    setHighlightId(order.id);
    setTimeout(() => {
      const el = rowRefs.current[order.id];
      if (el && typeof el.scrollIntoView === 'function') {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 50);
    setTimeout(() => setHighlightId((id) => (id === order.id ? null : id)), 2500);
  };

  return (
    <main className="min-h-screen bg-[#0B1220] text-white pb-32">
      {/* Header */}
      <header className="sticky top-0 z-20 backdrop-blur bg-[#0B1220]/80 border-b border-white/5">
        <div className="max-w-3xl mx-auto px-5 py-3 flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-emerald-500 flex items-center justify-center shadow-lg shadow-emerald-500/30">
            <Truck className="w-5 h-5 text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] font-bold tracking-[0.2em] text-emerald-300 uppercase">Driver</div>
            <div className="font-display text-lg truncate" data-testid="driver-name">{driver?.name || '...'}</div>
          </div>
          <div className="flex items-center gap-2">
            <NotificationsBell role="driver" authHeaders={authHeaders} variant="dark" />
            <PushBell role="driver" authHeaders={authHeaders} variant="dark" autoPrompt />
            <span
              className={`hidden sm:inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full ${
                gpsOk === false ? 'bg-red-500/15 text-red-300' : 'bg-emerald-500/15 text-emerald-300'
              }`}
              data-testid="gps-status"
            >
              <span className={`w-1.5 h-1.5 rounded-full ${gpsOk === false ? 'bg-red-400' : 'bg-emerald-400 animate-pulse'}`} />
              GPS {gpsOk === false ? 'off' : 'live'}
            </span>
            <Button
              onClick={load}
              variant="ghost"
              size="icon"
              className="text-slate-300 hover:bg-white/5 rounded-full"
              data-testid="driver-refresh"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
            <Button
              onClick={() => {
                logout();
                navigate('/driver/login', { replace: true });
              }}
              variant="ghost"
              size="icon"
              className="text-slate-300 hover:bg-red-500/10 hover:text-red-300 rounded-full"
              data-testid="driver-logout"
            >
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </header>

      <section className="max-w-3xl mx-auto px-5 pt-6">
        <PushEnableBanner authHeaders={authHeaders} />
        <PasskeyEnrollCard role="driver" authHeaders={authHeaders} variant="dark" />
        <div className="flex items-baseline justify-between mb-3 mt-4">
          <h2 className="font-display text-2xl">Active deliveries</h2>
          <span className="text-xs text-slate-400">{active.length} active</span>
        </div>

        {loading ? (
          <div className="text-slate-400 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
        ) : active.length === 0 ? (
          <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-10 text-center">
            <Package className="w-10 h-10 text-slate-600 mx-auto mb-3" />
            <p className="text-slate-400">No active deliveries right now.</p>
          </div>
        ) : (
          <div className="space-y-3" data-testid="driver-active-orders">
            <DriverPickupScanner active={active} history={done} onMatch={onScanMatch} />
            <MultiStopRoute orders={active} />
            {active.map((o) => {
              const action = NEXT_ACTIONS[o.status];
              const isHighlight = highlightId === o.id;
              return (
                <article
                  key={o.id}
                  ref={(el) => { if (el) rowRefs.current[o.id] = el; }}
                  className={`rounded-2xl border bg-[#111827] p-5 shadow-xl shadow-black/30 transition-shadow ${
                    isHighlight
                      ? 'border-emerald-400/80 ring-4 ring-emerald-500/30'
                      : 'border-white/10'
                  }`}
                  data-testid={`driver-order-${o.id}`}
                >
                  <div className="flex items-start justify-between gap-3 mb-3">
                    <div className="min-w-0">
                      <div className="text-[10px] font-bold tracking-[0.2em] text-slate-400 uppercase">
                        {o.order_number || o.id.slice(0, 8)}
                      </div>
                      <div className="font-display text-lg truncate mt-0.5">{orderPartyPrimary(o)}</div>
                      {orderPartyContact(o) && (
                        <div className="text-xs text-slate-400 truncate mt-0.5">Contact: {orderPartyContact(o)}</div>
                      )}
                    </div>
                    <span className={`px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider ${STATUS_PILL[o.status]}`}>
                      {STATUS_LABELS[o.status]}
                    </span>
                  </div>

                  <div className="space-y-2 text-sm text-slate-300">
                    {o.customer_phone && (
                      <a href={`tel:${o.customer_phone}`} className="flex items-center gap-2 hover:text-emerald-300">
                        <Phone className="w-4 h-4" /> {o.customer_phone}
                      </a>
                    )}
                    {o.location_lat && o.location_lng && (
                      <div className="flex items-start gap-2">
                        <MapPin className="w-4 h-4 mt-0.5 flex-shrink-0 text-emerald-300" />
                        <div className="flex-1 min-w-0">
                          <div className="text-slate-200 break-words leading-snug">
                            {o.location_address || `${o.location_lat.toFixed(5)}, ${o.location_lng.toFixed(5)}`}
                          </div>
                          <div className="mt-1 flex items-center gap-2 flex-wrap">
                            <button
                              type="button"
                              onClick={() => openGoogleMapsNav({ lat: o.location_lat, lng: o.location_lng })}
                              className="rounded-full bg-emerald-500 hover:bg-emerald-600 text-white text-[11px] font-bold px-3 py-1.5 inline-flex items-center gap-1.5 transition active:scale-95"
                              data-testid={`driver-navigate-${o.id}`}
                            >
                              <Navigation className="w-3.5 h-3.5" /> Navigate
                            </button>
                            <button
                              type="button"
                              onClick={() => setRouteOpen(routeOpen === o.id ? null : o.id)}
                              className={`rounded-full text-[11px] font-bold px-3 py-1.5 inline-flex items-center gap-1.5 transition ${
                                routeOpen === o.id
                                  ? 'bg-white/15 text-white'
                                  : 'bg-white/5 text-slate-200 hover:bg-white/10'
                              }`}
                              data-testid={`driver-route-toggle-${o.id}`}
                            >
                              <MapIcon className="w-3.5 h-3.5" />
                              {routeOpen === o.id ? 'Hide route' : 'Preview route'}
                            </button>
                          </div>
                        </div>
                      </div>
                    )}
                    {o.customer_address && (
                      <div className="text-slate-400 text-xs">{o.customer_address}</div>
                    )}
                  </div>

                  <div className="mt-3 rounded-xl bg-black/30 border border-white/5 p-3 text-xs space-y-1.5">
                    {o.items?.map((i, idx) => (
                      <div key={idx} className="flex justify-between gap-2">
                        <span className="text-slate-300 truncate">{i.qty} {i.uom || 'ctn'} × {i.name}</span>
                        <span className="text-slate-500 flex-shrink-0"><Rial /> {(i.price * i.qty).toFixed(3)}</span>
                      </div>
                    ))}
                    <div className="flex justify-between font-bold pt-2 border-t border-white/5">
                      <span>Total</span>
                      <span><Rial /> {(o.total || 0).toFixed(3)}</span>
                    </div>
                  </div>

                  <div className="mt-4 flex flex-wrap items-center gap-2">
                    {action && (
                      <Button
                        onClick={() => updateStatus(o, action.next)}
                        disabled={busyId === o.id}
                        className={`rounded-full text-white font-semibold ${action.tone}`}
                        data-testid={`driver-next-${o.id}`}
                      >
                        {busyId === o.id ? (
                          <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                        ) : (
                          <action.icon className="w-4 h-4 mr-1" />
                        )}
                        {action.label}
                      </Button>
                    )}
                    <button
                      onClick={() => setChatOpen(chatOpen === o.id ? null : o.id)}
                      className={`rounded-full text-xs font-bold px-3 py-2 inline-flex items-center gap-1.5 transition ${
                        chatOpen === o.id
                          ? 'bg-emerald-500 text-white'
                          : 'bg-white/5 text-slate-200 hover:bg-white/10'
                      }`}
                      data-testid={`driver-chat-toggle-${o.id}`}
                    >
                      <MessageCircle className="w-3.5 h-3.5" />
                      {chatOpen === o.id ? 'Hide chat' : 'Chat dispatch'}
                    </button>
                    {settings?.footer_phone && (
                      <>
                        <a
                          href={`tel:${settings.footer_phone}`}
                          className="rounded-full text-xs font-bold px-3 py-2 bg-white/5 text-slate-200 hover:bg-white/10 inline-flex items-center gap-1.5"
                          data-testid={`driver-call-dispatch-${o.id}`}
                        >
                          <Phone className="w-3.5 h-3.5" /> Call
                        </a>
                        <a
                          href={`https://wa.me/${settings.footer_phone.replace(/\D/g, '')}?text=${encodeURIComponent(`Re: order ${o.order_number || o.id.slice(0, 8)} — `)}`}
                          target="_blank"
                          rel="noreferrer"
                          className="rounded-full text-xs font-bold px-3 py-2 bg-white/5 text-slate-200 hover:bg-white/10 inline-flex items-center gap-1.5"
                          data-testid={`driver-wa-dispatch-${o.id}`}
                        >
                          <MessageCircle className="w-3.5 h-3.5" /> WhatsApp
                        </a>
                      </>
                    )}
                  </div>
                  {routeOpen === o.id && o.location_lat && o.location_lng && (
                    <div className="mt-3" data-testid={`driver-route-panel-${o.id}`}>
                      <RoutePreview
                        destination={{
                          lat: o.location_lat,
                          lng: o.location_lng,
                          label: orderPartyPrimary(o),
                        }}
                      />
                    </div>
                  )}
                  {chatOpen === o.id && (
                    <div className="mt-3">
                      <OrderChat
                        apiBase={API}
                        orderId={o.id}
                        role="driver"
                        authHeaders={authHeaders}
                        variant="dark"
                      />
                    </div>
                  )}
                </article>
              );
            })}
          </div>
        )}

        {done.length > 0 && (
          <>
            <h3 className="font-display text-lg mt-10 mb-3 text-slate-400">Recently completed</h3>
            <div className="space-y-2" data-testid="driver-completed-orders">
              {done.slice(0, 10).map((o) => (
                <div key={o.id} className="rounded-xl border border-white/5 bg-white/[0.02] px-4 py-3 flex items-center gap-3">
                  <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${STATUS_PILL[o.status]}`}>
                    {STATUS_LABELS[o.status]}
                  </span>
                  <div className="text-sm truncate flex-1">
                    {orderPartyPrimary(o)}
                    {orderPartyContact(o) && (
                      <span className="ml-1 text-[11px] text-slate-500">· {orderPartyContact(o)}</span>
                    )}
                  </div>
                  <div className="text-xs text-slate-500"><Rial /> {(o.total || 0).toFixed(3)}</div>
                </div>
              ))}
            </div>
          </>
        )}
      </section>
      <PWAInstallPrompt />
    </main>
  );
};

export default DriverDashboard;
